import {formatTime} from "@/src/modules/core/lib/formatters";
import {ShiftCoverageBreaks} from "@/src/modules/shifts/types";

export type CoverageBreakStatus =
  | {kind: "on_break"; label: string; tone: "warning"}
  | {kind: "overdue"; label: string; tone: "destructive"}
  | {kind: "due"; label: string; tone: "warning"}
  | {kind: "upcoming"; label: string; tone: "muted"}
  | {kind: "none"; label: null; tone: "muted"};

const NONE_STATUS: CoverageBreakStatus = {kind: "none", label: null, tone: "muted"};

/** Precedence: on break now → overdue → due → next scheduled → none. */
export function getCoverageBreakStatus(
  breaks: ShiftCoverageBreaks | undefined,
  now: Date = new Date(),
): CoverageBreakStatus {
  if (!breaks) return NONE_STATUS;

  if (breaks.on_break && breaks.current) {
    const startedAt = breaks.current.started_at;
    const minutes = startedAt
      ? Math.max(0, Math.floor((now.getTime() - new Date(startedAt).getTime()) / 60000))
      : null;
    const label =
      minutes !== null
        ? `On break · ${breaks.current.break_type} (${minutes}m)`
        : `On break · ${breaks.current.break_type}`;
    return {kind: "on_break", label, tone: "warning"};
  }

  if (breaks.summary.overdue > 0) {
    return {kind: "overdue", label: "Break overdue", tone: "destructive"};
  }

  if (breaks.summary.due > 0) {
    return {kind: "due", label: "Break due", tone: "warning"};
  }

  const upcoming = breaks.items
    .filter((item) => item.execution_status === "scheduled")
    .filter((item) => new Date(item.start_datetime).getTime() >= now.getTime())
    .sort((a, b) => new Date(a.start_datetime).getTime() - new Date(b.start_datetime).getTime())[0];

  if (upcoming) {
    return {kind: "upcoming", label: `Next break · ${formatTime(upcoming.start_datetime)}`, tone: "muted"};
  }

  return NONE_STATUS;
}
